/**
 * PresenterViewApp
 *
 * Entry point for the presenter window opened at
 * `/slide-sense/present?session=<uuid>`. Reads the `session` query
 * param, opens the corresponding BroadcastChannel via
 * `usePresentationSync`, and renders `PresenterView`.
 *
 * This component lives outside the normal app shell: it is mounted by
 * a dedicated top-level route so the presenter window does NOT show
 * the sidebar, module header, or any other app chrome.
 *
 * When the URL is missing `session` (e.g. the user typed the URL by
 * hand) we show a small error stub instead of silently rendering an
 * orphaned channel-less view.
 */

import { useEffect, useMemo } from "react"

import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { PresenterView } from "./PresenterView"
import { usePresentationSync } from "./use-presentation-sync"
import { usePresenterModes } from "../../hooks/use-module-config"

export function PresenterViewApp() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const sessionId = searchParams.get("session")
  const presenterModes = usePresenterModes()

  const sync = usePresentationSync(sessionId)

  // Set a window-level document title so the user can tell the two
  // windows apart in their OS window switcher / taskbar.
  useEffect(() => {
    const previous = document.title
    document.title = t("slideSense.present.documentTitle")
    return () => {
      document.title = previous
    }
  }, [t])

  // Memoize the no-session error so the `sync` hook still runs even
  // when we render an error view — keeping the early return simple.
  const errorContent = useMemo(() => {
    if (sessionId) return null
    return (
      <div
        className="bg-background text-foreground flex h-screen w-screen items-center justify-center"
        data-testid="presenter-view-error"
        role="alert"
      >
        <div className="flex max-w-md flex-col items-center gap-3 px-4 text-center">
          <h1 className="text-2xl font-semibold">{t("slideSense.present.sessionMissing")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("slideSense.present.sessionMissingDescription")}
          </p>
        </div>
      </div>
    )
  }, [sessionId, t])

  // Fail closed: if the tenant admin disabled Presenter View, block direct
  // URL access to this window even if a valid session id was supplied.
  // While the config query is in flight the default is "enabled", so a
  // legitimately-opened presenter window never flashes this stub. (#2336 H)
  const disabledContent = useMemo(() => {
    if (presenterModes.presenter_view) return null
    return (
      <div
        className="bg-background text-foreground flex h-screen w-screen items-center justify-center"
        data-testid="presenter-view-disabled"
        role="alert"
      >
        <div className="flex max-w-md flex-col items-center gap-3 px-4 text-center">
          <h1 className="text-2xl font-semibold">{t("slideSense.present.modeUnavailableTitle")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("slideSense.present.modeUnavailableDescription")}
          </p>
        </div>
      </div>
    )
  }, [presenterModes.presenter_view, t])

  if (errorContent) return errorContent
  if (disabledContent) return disabledContent

  return <PresenterView sync={sync} />
}
